import { parseCapacity } from '../../../player/src/shared/utils/capacity';

// Below this size, default product cards collapse to thumbnails only.
export const MAX_WIDTH_TO_SHOW_ONLY_THUMBNAILS = 220;
export const MAX_HEIGHT_TO_SHOW_ONLY_THUMBNAILS = 190;
export const PRODUCT_HYDRATION_TIMEOUT_MS = 3000;
export const INTERNAL_HYDRATED_PRODUCT_UPDATE_EVENT = 'internal-hydrated-product-update';

const hasPreviewProducts = ({ products, showProducts, settingsProducts }) => (
  showProducts !== 0
  && settingsProducts !== 0
  && Array.isArray(products)
  && products.length > 0
);

export const hasProductId = (id) => (
  (typeof id === 'string' && id !== '')
  || (typeof id === 'number' && Number.isFinite(id))
);

const toHydrationProductId = (product) => product?._id ?? product?.id;
const toProductIdKey = (id) => String(id);

const hasSameProductIds = ({ products, fallbackProducts }) => {
  if (!Array.isArray(products) || !Array.isArray(fallbackProducts)) return false;
  if (products.length !== fallbackProducts.length) return false;

  const productIds = products.map(({ id }) => id).filter(hasProductId).map(toProductIdKey);
  const fallbackProductIds = fallbackProducts.map(({ id }) => id).filter(hasProductId).map(toProductIdKey);
  if (productIds.length !== products.length || fallbackProductIds.length !== fallbackProducts.length) return false;

  const productIdSet = new Set(productIds);
  const fallbackProductIdSet = new Set(fallbackProductIds);
  return productIds.length === productIdSet.size
    && productIdSet.size === fallbackProductIdSet.size
    && productIds.every(id => fallbackProductIdSet.has(id));
};

export const parseProductData = (product, integrationConfig = {}) => {
  if (!product || typeof product !== 'object') return {};
  if (!product?.hydrated) {
    if (!product?.price || typeof product?.price !== 'object') return product;

    const { price, ...productWithoutPrice } = product || {};
    const { amount, ...priceWithoutAmount } = price || {};
    return {
      ...productWithoutPrice,
      price: {
        ...priceWithoutAmount,
        current: amount,
      },
    };
  }

  if (product?.hydrated?._hidden) return {};

  const hydrated = product?.hydrated?.product || {};
  const { variations = [], defaultVariationIndex } = hydrated;

  if (!variations?.length) return product;

  const title = hydrated?.name;
  const brand = hydrated?.brandName;
  const defaultVariation = variations[defaultVariationIndex] || variations[0] || {};
  const thumbnail = product.thumbnail || defaultVariation?.imageUrls?.[0];
  const sizes = defaultVariation.sizes || [];
  const cheapestPricedSize = sizes.reduce((cheapest, size) => {
    const current = Number(size?.price?.current);
    const cheapestCurrent = Number(cheapest?.price?.current);
    if (!Number.isFinite(current) || current <= 0) return cheapest;
    return !Number.isFinite(cheapestCurrent) || current < cheapestCurrent ? size : cheapest;
  }, null);
  const capacity = parseCapacity((cheapestPricedSize || sizes[0])?.capacity);

  const { current, currency, discountPercentage, original, perUnit, unitAmount, unitDisplayName } = (() => {
    const {
      current = 0,
      currency,
      discountPercentage,
      original,
      perUnit,
      unitAmount,
      unitDisplayName,
    } = cheapestPricedSize?.price || {};
    if (!current) return {};
    return {
      current: Number(current),
      currency: currency || integrationConfig?.currency,
      discountPercentage,
      original: Number(original),
      perUnit: Number(perUnit),
      unitAmount,
      unitDisplayName,
    };
  })();

  return {
    ...product,
    ...(title && { title }),
    ...(brand && { brand }),
    ...(current && {
      price: {
        current,
        currency,
        discountPercentage,
        original,
        perUnit,
        unitAmount,
        unitDisplayName,
      },
    }),
    ...(capacity && { capacity }),
    ...(thumbnail && { thumbnail }),
  };
};

export const getPreviewProductsToHydrate = ({
  products,
  showProducts,
  settingsProducts,
  hasProductHydrationCallbacks,
}) => {
  if (!hasProductHydrationCallbacks) return [];
  if (!hasPreviewProducts({ products, showProducts, settingsProducts })) return [];

  return products
    .map(({ productReference: ref, publicUrl: url, id }) => ({ ref, url, id }))
    .filter(({ id }) => hasProductId(id));
};

export const getRenderablePreviewProducts = ({
  products,
  productsToHydrate,
  fallbackProducts,
}) => {
  if (!Array.isArray(products)) return [];

  const pendingProductIds = new Set(
    (Array.isArray(productsToHydrate) ? productsToHydrate : [])
      .map(({ id }) => id)
      .filter(hasProductId)
      .map(toProductIdKey)
  );
  if (!pendingProductIds.size) return products;

  // Hydration can mark products hidden, so pending hydrated products are not
  // renderable until the hydration result or timeout settles.
  const renderableProducts = products.filter(({ id }) => !pendingProductIds.has(toProductIdKey(id)));
  return renderableProducts.length || !hasSameProductIds({ products, fallbackProducts })
    ? renderableProducts
    : fallbackProducts;
};

export const applyHydratedPreviewProducts = ({ products, hydratedProducts }) => {
  if (!Array.isArray(products)) return [];
  if (!Array.isArray(hydratedProducts) || hydratedProducts.length === 0) return products;

  const hydratedProductsById = hydratedProducts.reduce((map, hydratedProduct) => {
    const id = toHydrationProductId(hydratedProduct);
    return hasProductId(id) ? map.set(toProductIdKey(id), hydratedProduct) : map;
  }, new Map());

  return products.map((product) => {
    const hydrated = hydratedProductsById.get(toProductIdKey(product?.id));
    return hydrated ? { ...product, hydrated } : product;
  });
};

export const getRemainingProductsToHydrate = ({ productsToHydrate, hydratedProducts }) => {
  if (!Array.isArray(productsToHydrate)) return [];
  if (!Array.isArray(hydratedProducts) || hydratedProducts.length === 0) return productsToHydrate;

  const hydratedProductIds = hydratedProducts.reduce((set, hydratedProduct) => {
    const id = toHydrationProductId(hydratedProduct);
    return hasProductId(id) ? set.add(toProductIdKey(id)) : set;
  }, new Set());

  return productsToHydrate.filter(({ id }) => !hydratedProductIds.has(toProductIdKey(id)));
};

export const removeProductHydrationCallback = ({ callbacks, callbackToRemove }) => (
  Array.isArray(callbacks)
    ? callbacks.filter(callback => callback !== callbackToRemove)
    : []
);

export const shouldShowOnlyProductThumbnails = ({
  productCardMode,
  containerWidth,
  containerHeight,
}) => (
  productCardMode === 'default'
  && Number.isFinite(containerWidth)
  && Number.isFinite(containerHeight)
  && (
    containerWidth < MAX_WIDTH_TO_SHOW_ONLY_THUMBNAILS
    || containerHeight < MAX_HEIGHT_TO_SHOW_ONLY_THUMBNAILS
  )
);
